import { Lead } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { TempPill, NeutralStagePill, OutcomeIcon, DealChip } from '@/components/leads/LeadVisual';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MoreHorizontal,
  Phone,
  MessageCircle,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  Calendar,
  CheckCircle2,
} from 'lucide-react';
import { format, isPast } from 'date-fns';
import { cn } from '@/lib/utils';

interface LeadsTableProps {
  leads: (Lead & { workspace_name?: string })[];
  onView: (lead: Lead) => void;
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onCreateDeal?: (lead: Lead) => void;
  onMarkDone?: (lead: Lead) => void;
  highlightedLeadId?: string | null;
  showWorkspaceColumn?: boolean; // Show workspace name column (for 'all' mode)
}

export function LeadsTable({ leads, onView, onEdit, onDelete, onCreateDeal, onMarkDone, highlightedLeadId, showWorkspaceColumn = false }: LeadsTableProps) {
  const { data: deals } = useDeals();
  const handleWhatsApp = (lead: Lead, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!lead.phone) return;
    const cleanPhone = lead.phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Hi ${lead.name}, following up on our conversation.`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const handleCall = (lead: Lead, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!lead.phone) return;
    window.open(`tel:${lead.phone}`, '_self');
  };

  return (
    <div className="border border-border/40 rounded-xl bg-card overflow-hidden shadow-sm">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm">
          <TableRow className="hover:bg-transparent border-b border-border/40">
            <TableHead className="font-semibold">Name</TableHead>
            {showWorkspaceColumn && (
              <TableHead className="hidden md:table-cell font-semibold">Workspace</TableHead>
            )}
            <TableHead className="hidden md:table-cell font-semibold">Contact</TableHead>
            <TableHead className="font-semibold">Temp</TableHead>
            <TableHead className="hidden sm:table-cell font-semibold">Stage</TableHead>
            <TableHead className="hidden lg:table-cell font-semibold">Source</TableHead>
            <TableHead className="hidden lg:table-cell font-semibold">Follow-up</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead) => {
            const isOverdue = lead.next_action_at && isPast(new Date(lead.next_action_at));
            const dealValue = deals?.find(d => d.lead_id === lead.id && d.stage !== 'won' && d.stage !== 'lost')?.value;
            const isWon = lead.pipeline_stage === 'won';
            const isLost = lead.pipeline_stage === 'lost';
            const isCompleted =
              lead.is_completed === true ||
              (lead as Lead & { status?: string; completed_at?: string | null }).status === 'done' ||
              (lead as Lead & { completed_at?: string | null }).completed_at != null;
            const outcome = isWon ? 'won' : isLost ? 'lost' : null;
            const isHighlighted = highlightedLeadId === lead.id;

            return (
              <TableRow
                id={`lead-row-${lead.id}`}
                key={lead.id}
                className={cn(
                  "table-row-interactive group transition-all duration-150 cursor-pointer",
                  isHighlighted && "bg-primary/10 border-l-4 border-l-primary ring-2 ring-primary/20",
                  !isHighlighted && isWon && "border-l-2 border-l-emerald-500/30",
                  !isHighlighted && isLost && !isCompleted && "border-l-2 border-l-rose-500/30",
                  isCompleted && "opacity-60"
                )}
                onClick={() => onView(lead)}
              >
              <TableCell>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <OutcomeIcon outcome={outcome} />
                      <p className={cn(
                        "font-medium text-foreground",
                        (isLost || isCompleted) && "text-muted-foreground line-through"
                      )}>
                        {lead.name}
                      </p>
                      <DealChip value={dealValue ?? null} currency="EUR" />
                    </div>
                    {lead.notes_short && !isLost && !isCompleted && (
                      <p className="text-xs text-muted-foreground truncate max-w-48 mt-0.5">
                        {lead.notes_short}
                      </p>
                    )}
                  </div>
                </TableCell>
                {showWorkspaceColumn && (
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="outline" className="text-xs">
                      {(lead as any).workspace_name || 'Unknown'}
                    </Badge>
                  </TableCell>
                )}
                <TableCell className="hidden md:table-cell">
                  <div className="flex items-center gap-1">
                    {lead.phone && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-60 hover:opacity-100 hover:bg-primary/10 hover:text-primary"
                            onClick={(e) => handleCall(lead, e)}
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Call {lead.name}</TooltipContent>
                      </Tooltip>
                    )}
                    {lead.phone && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-60 hover:opacity-100 hover:bg-[#25D366]/10 hover:text-[#25D366]"
                            onClick={(e) => handleWhatsApp(lead, e)}
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>WhatsApp {lead.name}</TooltipContent>
                      </Tooltip>
                    )}
                    <span className="text-sm text-muted-foreground ml-1">
                      {lead.email || lead.phone || 'No contact'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <TempPill value={lead.temperature as "hot" | "warm" | "cold" | "stale" | null} />
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {lead.pipeline_stage &&
                    lead.pipeline_stage !== "won" &&
                    lead.pipeline_stage !== "lost" && (
                      <NeutralStagePill stage={lead.pipeline_stage as any} />
                    )}
                </TableCell>
                <TableCell className="hidden lg:table-cell capitalize text-sm text-muted-foreground">
                  {lead.source || '-'}
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  {lead.next_action_at ? (
                    <div className={cn(
                      "flex items-center gap-1.5 text-sm",
                      isOverdue ? "text-destructive font-medium" : "text-muted-foreground"
                    )}>
                      <Calendar className={cn("w-3.5 h-3.5", isOverdue && "text-destructive")} />
                      {format(new Date(lead.next_action_at), 'MMM d')}
                      {isOverdue && (
                        <span className="text-xs bg-destructive/10 px-1.5 py-0.5 rounded text-destructive">
                          Overdue
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground/50">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => onView(lead)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEdit(lead)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Lead
                      </DropdownMenuItem>
                      {onCreateDeal && (
                        <DropdownMenuItem onClick={() => onCreateDeal(lead)}>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Create Deal
                        </DropdownMenuItem>
                      )}
                      {onMarkDone && (
                        <DropdownMenuItem
                          onClick={() => onMarkDone(lead)}
                          disabled={isCompleted}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Mark done
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDelete(lead)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}